import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { setUnauthorizedHandler } from "./api";
import { clearToken, getToken, isMiniApp, setToken } from "./auth";

type AuthMode = "miniapp" | "web";

type Auth = {
  mode: AuthMode;
  /** Готов ли запрашивать защищённые эндпоинты (есть initData или токен). */
  authenticated: boolean;
  login: (token: string) => void;
  logout: () => void;
};

const AuthContext = createContext<Auth | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const mode: AuthMode = isMiniApp() ? "miniapp" : "web";
  // В Mini App аутентификация всегда через initData; в вебе — по наличию токена.
  const [authenticated, setAuthenticated] = useState<boolean>(
    mode === "miniapp" || !!getToken(),
  );

  const login = useCallback((token: string) => {
    setToken(token);
    setAuthenticated(true);
  }, []);

  const logout = useCallback(() => {
    clearToken();
    setAuthenticated(false);
  }, []);

  useEffect(() => {
    if (mode !== "web") return;
    setUnauthorizedHandler(() => setAuthenticated(false));
    return () => setUnauthorizedHandler(null);
  }, [mode]);

  const value = useMemo<Auth>(
    () => ({ mode, authenticated, login, logout }),
    [mode, authenticated, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): Auth {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
